import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  SPECIES_CACHE_SESSION_PREFIX,
  SPECIES_CACHE_SESSION_PREFIX_LEGACY,
} from "@/constants/browserStorageKeys";
import { migrateSessionStorageKey } from "@/utils/migrateBrowserStorageKey";
import { getAllSpecies } from "../utils/speciesUtils";
import { Species } from "../data/species/types/Species";
import {
  filterCatalogSpecies,
  isForagePrimarySpecies,
} from "@/utils/speciesForagePrimary";
import {
  getAllCachedPreviews,
  getCachedPreviewSpecies,
  registerPreviewSpecies as cacheRegisterPreviewSpecies,
} from "@/utils/species/speciesPreviewCache";

type SpeciesView = "full" | "summary";

const SPECIES_CACHE_TTL_MS = 15 * 60 * 1000;
const speciesMemoryCache = new Map<
  SpeciesView,
  { species: Species[]; savedAt: number }
>();

function getSpeciesCacheKey(view: SpeciesView): string {
  return `${SPECIES_CACHE_SESSION_PREFIX}${view}`;
}

function readSpeciesCache(
  view: SpeciesView,
): { species: Species[]; isStale: boolean } | null {
  const memoryEntry = speciesMemoryCache.get(view);
  if (memoryEntry) {
    return {
      species: memoryEntry.species,
      isStale: Date.now() - memoryEntry.savedAt > SPECIES_CACHE_TTL_MS,
    };
  }

  if (view === "summary") {
    const fullEntry = speciesMemoryCache.get("full");
    if (fullEntry) {
      return {
        species: fullEntry.species,
        isStale: Date.now() - fullEntry.savedAt > SPECIES_CACHE_TTL_MS,
      };
    }
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    migrateSessionStorageKey(
      getSpeciesCacheKey(view),
      `${SPECIES_CACHE_SESSION_PREFIX_LEGACY}${view}`,
    );
    migrateSessionStorageKey(
      `${SPECIES_CACHE_SESSION_PREFIX}full`,
      `${SPECIES_CACHE_SESSION_PREFIX_LEGACY}full`,
    );
    const raw = window.sessionStorage.getItem(getSpeciesCacheKey(view));
    if (!raw) {
      if (view === "summary") {
        const fullRaw = window.sessionStorage.getItem(
          getSpeciesCacheKey("full"),
        );
        if (!fullRaw) return null;
        const parsedFull = JSON.parse(fullRaw) as {
          species?: Species[];
          savedAt?: number;
        };
        if (
          !Array.isArray(parsedFull.species) ||
          typeof parsedFull.savedAt !== "number"
        ) {
          return null;
        }
        speciesMemoryCache.set("full", {
          species: parsedFull.species,
          savedAt: parsedFull.savedAt,
        });
        return {
          species: parsedFull.species,
          isStale: Date.now() - parsedFull.savedAt > SPECIES_CACHE_TTL_MS,
        };
      }
      return null;
    }

    const parsed = JSON.parse(raw) as { species?: Species[]; savedAt?: number };
    if (!Array.isArray(parsed.species) || typeof parsed.savedAt !== "number") {
      return null;
    }

    speciesMemoryCache.set(view, {
      species: parsed.species,
      savedAt: parsed.savedAt,
    });
    return {
      species: parsed.species,
      isStale: Date.now() - parsed.savedAt > SPECIES_CACHE_TTL_MS,
    };
  } catch {
    return null;
  }
}

function writeSpeciesCache(view: SpeciesView, species: Species[]) {
  const entry = { species, savedAt: Date.now() };
  speciesMemoryCache.set(view, entry);

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      getSpeciesCacheKey(view),
      JSON.stringify(entry),
    );
  } catch {
    // ignore storage failures
  }
}

interface SpeciesContextType {
  species: Species[];
  /** GBIF/Wikipedia preview profiles registered this session. */
  previewSpecies: Species[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Load the catalog if it hasn't been requested yet (for `fetchOnMount={false}`). */
  ensureLoaded: () => void;
  findByName: (name: string) => Species | undefined;
  findByTaxonId: (taxonId: number) => Species | undefined;
  findBySlug: (slug: string) => Species | undefined;
  registerPreviewSpecies: (species: Species) => void;
  getSpeciesForMap: (
    taxonId: number | null | undefined,
    opts?: { slug?: string; name?: string },
  ) => Species | null;
}

const SpeciesContext = createContext<SpeciesContextType | undefined>(undefined);

export const useSpeciesContext = () => {
  const context = useContext(SpeciesContext);
  if (!context) {
    throw new Error("useSpeciesContext must be used within a SpeciesProvider");
  }
  return context;
};

interface SpeciesProviderProps {
  children: React.ReactNode;
  view?: SpeciesView;
  /**
   * `false`: don't request the catalog until `ensureLoaded()` is called. For
   * routes that render from a smaller request (species detail, WN-223), so the
   * ~440 KB catalog doesn't compete with the page's photo. A session cache is
   * still used when present.
   */
  fetchOnMount?: boolean;
}

export const SpeciesProvider: React.FC<SpeciesProviderProps> = ({
  children,
  view = "full",
  fetchOnMount = true,
}) => {
  const cachedSpecies = readSpeciesCache(view);
  /** Primitive — `readSpeciesCache` returns a new object every call; never put that object in effect deps. */
  const hasUsableSessionCache = Boolean(
    cachedSpecies && !cachedSpecies.isStale,
  );
  const [species, setSpecies] = useState<Species[]>(
    () => filterCatalogSpecies(cachedSpecies?.species ?? []),
  );
  const [previewSpecies, setPreviewSpecies] = useState<Species[]>(() =>
    filterCatalogSpecies(getAllCachedPreviews()),
  );
  const [loading, setLoading] = useState(() => !cachedSpecies && fetchOnMount);
  const fetchRequestedRef = React.useRef(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSpecies = useCallback(async () => {
    try {
      setLoading((prev) => prev || species.length === 0);
      setError(null);
      const data = await getAllSpecies({ summary: view === "summary" });
      writeSpeciesCache(view, data);
      setSpecies(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch species");
      setSpecies([]);
    } finally {
      setLoading(false);
    }
  }, [species.length, view]);

  const findByName = useCallback(
    (name: string) => {
      return species.find((s) => s.name.toLowerCase() === name.toLowerCase());
    },
    [species],
  );

  const findByTaxonId = useCallback(
    (taxonId: number) => {
      return (
        species.find((s) => s.taxonId === taxonId) ??
        previewSpecies.find((s) => s.taxonId === taxonId)
      );
    },
    [species, previewSpecies],
  );

  const findBySlug = useCallback(
    (slug: string) => {
      const key = slug.trim().toLowerCase();
      return (
        species.find((s) => s.slug?.trim().toLowerCase() === key) ??
        previewSpecies.find((s) => s.slug?.trim().toLowerCase() === key) ??
        getCachedPreviewSpecies({ slug: key }) ??
        undefined
      );
    },
    [species, previewSpecies],
  );

  const registerPreviewSpecies = useCallback((entry: Species) => {
    if (!isForagePrimarySpecies(entry)) return;
    cacheRegisterPreviewSpecies(entry);
    setPreviewSpecies(filterCatalogSpecies(getAllCachedPreviews()));
  }, []);

  const getSpeciesForMap = useCallback(
    (
      taxonId: number | null | undefined,
      opts?: { slug?: string; name?: string },
    ): Species | null => {
      if (taxonId != null) {
        const fromCatalog = species.find((s) => s.taxonId === taxonId);
        if (fromCatalog) return fromCatalog;
        const fromPreview =
          previewSpecies.find((s) => s.taxonId === taxonId) ??
          getCachedPreviewSpecies({ taxonId });
        if (fromPreview) return fromPreview;
      }
      if (opts?.slug?.trim()) {
        const bySlug = findBySlug(opts.slug);
        if (bySlug) return bySlug;
      }
      if (opts?.name?.trim()) {
        const nameLower = opts.name.trim().toLowerCase();
        const match =
          species.find((s) => s.name.toLowerCase() === nameLower) ??
          previewSpecies.find((s) => s.name.toLowerCase() === nameLower);
        if (match) return match;
      }
      return null;
    },
    [species, previewSpecies, findBySlug],
  );

  useEffect(() => {
    if (hasUsableSessionCache) {
      setLoading(false);
      return;
    }
    if (!fetchOnMount) return;

    fetchRequestedRef.current = true;
    fetchSpecies();
  }, [view, hasUsableSessionCache, fetchSpecies, fetchOnMount]);

  const ensureLoaded = useCallback(() => {
    if (fetchRequestedRef.current || hasUsableSessionCache) return;
    fetchRequestedRef.current = true;
    void fetchSpecies();
  }, [hasUsableSessionCache, fetchSpecies]);

  const value: SpeciesContextType = {
    species,
    previewSpecies,
    loading,
    error,
    refetch: fetchSpecies,
    ensureLoaded,
    findByName,
    findByTaxonId,
    findBySlug,
    registerPreviewSpecies,
    getSpeciesForMap,
  };

  return (
    <SpeciesContext.Provider value={value}>{children}</SpeciesContext.Provider>
  );
};
